import { useState, type FormEvent } from 'react';
import { Clock, LogIn } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { ApiError } from '../lib/api';
import { Banner, Button, Field, Input, useUi } from '../components/ui';
import { isAutoClockDevice, takeClockFlash } from './timeclock/autoClock';

export default function Login() {
  const { login } = useAuth();
  const { toast } = useUi();
  // Meldung der letzten Abmeldung („Gehen gestempelt …“) – nur einmal zeigen
  const [flash] = useState(takeClockFlash);
  const [autoClock] = useState(isAutoClockDevice);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needTotp, setNeedTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const clock = await login(username.trim(), password, needTotp ? totp : undefined);
      if (clock) toast(clock.text, clock.tone);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'TOTP_REQUIRED') {
        setNeedTotp(true);
      } else {
        setError(err instanceof Error ? err.message : 'Anmeldung fehlgeschlagen');
        if (err instanceof ApiError && err.code === 'TOTP_INVALID') setTotp('');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-page">
      <form className="auth-card stack loose" onSubmit={submit}>
        <div className="row" style={{ gap: 12 }}>
          <img src="/icon.svg" width={40} height={40} alt="" />
          <div>
            <h1>Kiosk Kasse</h1>
            <div className="muted small">Bitte anmelden</div>
          </div>
        </div>
        {flash && !error && <Banner tone="good">{flash}</Banner>}
        {error && <Banner tone="crit">{error}</Banner>}
        {!needTotp ? (
          <>
            <Field label="Benutzername" htmlFor="u">
              <Input id="u" autoFocus autoComplete="username" autoCapitalize="none" value={username} onChange={(e) => setUsername(e.target.value)} required />
            </Field>
            <Field label="Passwort" htmlFor="p">
              <Input id="p" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </Field>
          </>
        ) : (
          <Field label="Code aus der Authenticator-App" htmlFor="t" hint="6-stelliger Code, z. B. aus Google Authenticator">
            <Input id="t" autoFocus inputMode="numeric" autoComplete="one-time-code" maxLength={6} size_="lg" value={totp} onChange={(e) => setTotp(e.target.value.replace(/\D/g, ''))} />
          </Field>
        )}
        <Button type="submit" variant="primary" size="lg" block loading={busy} icon={<LogIn size={18} />}>Anmelden</Button>
        {needTotp && <Button variant="ghost" onClick={() => { setNeedTotp(false); setTotp(''); }}>Zurück</Button>}
        {autoClock && (
          <div className="xs subtle" style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
            <Clock size={14} aria-hidden style={{ flex: 'none', marginTop: 1 }} />
            <span>An diesem Gerät wird beim Anmelden automatisch „Kommen“ und beim Abmelden „Gehen“ gestempelt.</span>
          </div>
        )}
      </form>
    </div>
  );
}
